import RetryImage from '~/components/image/RetryImage';
import { EmptyView } from '../EmptyView';
import { api } from '~/trpc-extra/api';
import { useAtomValue } from 'jotai';
import { selectedOperationIdAtom } from '~/atoms/no-code-atoms';
import { ActionIcon, Loader, Tabs } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconMaximize } from '@tabler/icons-react';

export type DirectScreenshots = {
  before?: string;
  action?: string;
  after?: string;
};

/**
 * Loads the images preview for the current selectedOperationId or the operationId passed in.
 * Can also accept a direct screenshot URL for errored operations.
 * Can also accept multiple direct screenshot URLs with before/action/after tabs.
 * By default, it will load the action screenshot.
 */
export default function ImageView({
  operationId,
  onMiniImageClick,
  directScreenshotUrl,
  directScreenshots,
}: {
  operationId?: string;
  onMiniImageClick?: () => void;
  /** Single direct screenshot URL (no tabs) */
  directScreenshotUrl?: string;
  /** Multiple direct screenshot URLs with before/action/after tabs */
  directScreenshots?: DirectScreenshots;
}) {
  const selectedOperationId = useAtomValue(selectedOperationIdAtom);

  const hasDirectScreenshots =
    directScreenshots && (directScreenshots.before || directScreenshots.action || directScreenshots.after);

  const { data: operation, isLoading } = api.operation.getImages.useQuery(
    {
      operationId: operationId ?? selectedOperationId ?? '',
    },
    {
      enabled: !directScreenshotUrl && !hasDirectScreenshots && (!!operationId || !!selectedOperationId),
    },
  );

  // If multiple direct screenshots are provided, show tabs
  if (hasDirectScreenshots) {
    const allScreenshots = [
      { label: 'Before', value: 'before', url: directScreenshots.before },
      { label: 'Action', value: 'action', url: directScreenshots.action },
      { label: 'After', value: 'after', url: directScreenshots.after },
    ];
    const availableScreenshots = allScreenshots.filter(
      (screenshot): screenshot is { label: string; value: string; url: string } => screenshot.url != null,
    );

    if (availableScreenshots.length === 0) {
      return <EmptyView className="min-h-[80vh]" />;
    }

    const maximizeImage = (screenshotUrl: string, screenshotLabel: string) => {
      onMiniImageClick?.();
      modals.open({
        transitionProps: { transition: 'pop' },
        centered: true,
        zIndex: 310,
        children: (
          <div className="flex h-full w-full items-center justify-center">
            <RetryImage
              key={`modal-direct-${screenshotUrl}`}
              src={screenshotUrl}
              alt={`${screenshotLabel} Browser Screen`}
              loadingBgComponent={<EmptyView className="min-h-[80vh]" />}
              className="object-contain"
            />
          </div>
        ),
        size: 'auto',
      });
    };

    // Default tab preference: Action > After > Before
    const defaultImageTab =
      availableScreenshots.find((screenshot) => screenshot.value === 'action')?.value ??
      availableScreenshots.find((screenshot) => screenshot.value === 'after')?.value ??
      availableScreenshots.find((screenshot) => screenshot.value === 'before')?.value;

    return (
      <div className="relative h-full w-full bg-white dark:bg-gray-900">
        <Tabs
          defaultValue={defaultImageTab}
          className="h-full"
          variant="outline"
        >
          <Tabs.List className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            {availableScreenshots.map((screenshot) => (
              <Tabs.Tab
                key={screenshot.value}
                value={screenshot.value}
                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {screenshot.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {availableScreenshots.map((screenshot) => (
            <Tabs.Panel
              key={screenshot.value}
              value={screenshot.value}
              className="h-[calc(100%-28px)] bg-white dark:bg-gray-900"
            >
              <ActionIcon
                variant="subtle"
                size="sm"
                color="gray"
                onClick={() => maximizeImage(screenshot.url, screenshot.label)}
                className="absolute right-0 top-0"
                title="Maximize image"
              >
                <IconMaximize size={16} />
              </ActionIcon>

              <RetryImage
                key={`direct-${screenshot.value}-${screenshot.url}`}
                src={screenshot.url}
                alt={`${screenshot.label} Browser Screen`}
                loadingBgComponent={<EmptyView className="min-h-[80vh]" />}
                className="cursor-pointer"
                onClick={() => maximizeImage(screenshot.url, screenshot.label)}
              />
            </Tabs.Panel>
          ))}
        </Tabs>
      </div>
    );
  }

  // If a single direct screenshot URL is provided, use it directly without fetching from API
  if (directScreenshotUrl) {
    const maximizeImage = (screenshotUrl: string, screenshotLabel: string) => {
      if (!screenshotUrl) {
        return;
      }

      onMiniImageClick?.();
      modals.open({
        transitionProps: { transition: 'pop' },
        centered: true,
        zIndex: 310,
        children: (
          <div className="flex h-full w-full items-center justify-center">
            <RetryImage
              key={`modal-direct-${screenshotUrl}`}
              src={screenshotUrl}
              alt={`${screenshotLabel} Browser Screen`}
              loadingBgComponent={<EmptyView className="min-h-[80vh]" />}
              className="object-contain"
            />
          </div>
        ),
        size: 'auto',
      });
    };

    return (
      <div className="relative h-full w-full bg-white dark:bg-gray-900">
        <ActionIcon
          variant="subtle"
          size="sm"
          color="gray"
          onClick={() => maximizeImage(directScreenshotUrl, 'Live Screenshot at Failure')}
          className="absolute right-0 top-0 z-10"
          title="Maximize image"
        >
          <IconMaximize size={16} />
        </ActionIcon>
        <RetryImage
          key={`direct-${directScreenshotUrl}`}
          src={directScreenshotUrl}
          alt="Live Screenshot at Failure"
          loadingBgComponent={<EmptyView className="min-h-[80vh]" />}
          className="cursor-pointer"
          onClick={() => maximizeImage(directScreenshotUrl, 'Live Screenshot at Failure')}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[80vh] w-full items-center justify-center bg-[var(--mantine-color-body)]">
        <Loader />
      </div>
    );
  }

  if (!operation) {
    return <EmptyView className="min-h-[80vh]" />;
  }

  const allOperationScreenshots = [
    { label: 'Before', value: 'before', url: operation.beforeScreenshot },
    { label: 'Action', value: 'action', url: operation.actionScreenshot },
    { label: 'After', value: 'after', url: operation.afterScreenshot },
  ];
  const availableScreenshots = allOperationScreenshots.filter(
    (screenshot): screenshot is { label: string; value: string; url: string } => screenshot.url != null,
  );

  if (availableScreenshots.length === 0) {
    return <EmptyView className="min-h-[80vh]" />;
  }

  const maximizeImage = (screenshotUrl: string, screenshotLabel: string) => {
    onMiniImageClick?.();
    modals.open({
      transitionProps: { transition: 'pop' },
      centered: true,
      zIndex: 310,
      children: (
        <div className="flex h-full w-full items-center justify-center">
          <RetryImage
            key={`modal-${operationId ?? selectedOperationId ?? ''}`}
            src={screenshotUrl}
            alt={`${screenshotLabel} Browser Screen`}
            loadingBgComponent={<EmptyView className="min-h-[80vh]" />}
            className="object-contain"
          />
        </div>
      ),
      size: 'auto',
    });
  };

  // Default tab preference: Action > After > Before
  const defaultImageTab =
    availableScreenshots.find((screenshot) => screenshot.value === 'action')?.value ??
    availableScreenshots.find((screenshot) => screenshot.value === 'after')?.value ??
    availableScreenshots.find((screenshot) => screenshot.value === 'before')?.value;

  return (
    <div className="relative h-full w-full bg-white dark:bg-gray-900">
      <Tabs
        defaultValue={defaultImageTab}
        className="h-full"
        variant="outline"
      >
        <Tabs.List className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          {availableScreenshots.map((screenshot) => (
            <Tabs.Tab
              key={screenshot.value}
              value={screenshot.value}
              className="px-3 py-1 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {screenshot.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {availableScreenshots.map((screenshot) => (
          <Tabs.Panel
            key={screenshot.value}
            value={screenshot.value}
            className="h-[calc(100%-28px)] bg-white dark:bg-gray-900"
          >
            {/* Discrete Button to maximize the image */}
            <ActionIcon
              variant="subtle"
              size="sm"
              color="gray"
              onClick={() => maximizeImage(screenshot.url, screenshot.label)}
              className="absolute right-0 top-0"
              title="Maximize image"
            >
              <IconMaximize size={16} />
            </ActionIcon>

            <RetryImage
              key={`${operationId ?? selectedOperationId ?? ''}-${screenshot.value}`}
              src={screenshot.url}
              alt={`${screenshot.label} Browser Screen`}
              loadingBgComponent={<EmptyView className="min-h-[80vh]" />}
              className="cursor-pointer"
              onClick={() => maximizeImage(screenshot.url, screenshot.label)}
            />
          </Tabs.Panel>
        ))}
      </Tabs>
    </div>
  );
}
